import { captureException, SentrySources } from 'utils/sentry'
import { ServiceTypeName } from 'fhir/HealthcareService/constants'
import { CustomFeatureConfigKey } from './constants'
import json from '../../../../feature-config/configs.json'

export const checkFeatureConfig = (key: CustomFeatureConfigKey, value?: string): boolean => {
  const config = json.configs?.[key]

  if (!config) {
    captureException(new Error(`There is no feature config for key: ${key}`), {
      tags: { source: SentrySources.FEATURE_CONFIGS }
    })
    return false
  }

  // Check if the feature is active
  if (!config.active || !('rule' in config)) {
    return config.default
  }

  // Check if the rule exists
  if (value && config.rule.variants.includes(value)) {
    return config.rule.serve
  } else {
    return config.rule.default
  }
}

export const checkFeatureConfigByServiceTypes = (
  key: CustomFeatureConfigKey,
  serviceTypes: ServiceTypeName[] | undefined
): boolean => {
  // NOTE: technically we could use check feature config directly, but this is more readable
  // and is abstracted at the right level, once we introduce checking features by other types of
  // values such as user category, etc.
  for (const serviceType of serviceTypes ?? []) {
    if (checkFeatureConfig(key, serviceType)) {
      return true
    }
  }

  // Handle special case of empty service types
  if (serviceTypes?.length === 0) {
    return checkFeatureConfig(key, undefined)
  }

  return false
}

/**
 * Checks if a feature configuration is active for all service types.
 *
 * @param key - The feature configuration key to check
 * @param serviceTypes - An array of service types to check against
 * @returns True if the feature is active for all service types, false otherwise
 *
 * If no service types are provided, checks the default configuration.
 * If any service type has the feature disabled, returns false.
 */
export const featureConfigIsActiveForAllServiceTypes = (
  key: CustomFeatureConfigKey,
  serviceTypes: ServiceTypeName[] | undefined
): boolean => {
  if (!serviceTypes) {
    return checkFeatureConfig(key, undefined)
  }

  for (const serviceType of serviceTypes ?? []) {
    if (!checkFeatureConfig(key, serviceType)) {
      return false
    }
  }

  return true
}
